import {
  MENU_LABEL_TEXT,
  MENU_PANEL,
  MENU_ROW,
  MENU_RULE,
} from "@rosc/ui/v32/menu";
import { SELECT_TRIGGER } from "@rosc/ui/v32/select";
import { AppConfig } from "@/common/appConfig";

// The shared shadcn pieces in the v3.2 app (VITE_APP_REDESIGN_ENABLED): each
// adds these classes before the caller's own, so screens that are not
// rebuilt yet (Settings sections, template pages, dialogs) take the v3.2
// look, and a caller's className still wins. With the flag off nothing is
// added, so the classic app is unchanged. Read at render time (stories and
// tests can switch the flag).
export const underV3 = (classes: string): string | false =>
  AppConfig.enableAppRedesign === true && classes;

const PILL =
  "rounded-full font-v3 font-semibold shadow-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring";

export const V3_BUTTON_VARIANT = {
  default: `${PILL} bg-v3-ink text-v3-on-ink hover:bg-v3-ink hover:opacity-90`,
  primaryDark: `${PILL} bg-v3-ink text-v3-on-ink hover:bg-v3-ink hover:opacity-90`,
  success: `${PILL} bg-v3-ok text-white hover:bg-v3-ok hover:opacity-90`,
  destructive: `${PILL} bg-v3-rec text-white hover:bg-v3-rec hover:opacity-90`,
  outline: `${PILL} border-0 bg-v3-surface-2 text-v3-text shadow-v3-hairline hover:bg-v3-surface-3 hover:text-v3-text`,
  secondary: `${PILL} bg-v3-surface-2 text-v3-text shadow-v3-hairline hover:bg-v3-surface-3`,
  grey: `${PILL} bg-v3-surface-2 text-v3-text shadow-v3-hairline hover:bg-v3-surface-3`,
  ghost: `${PILL} text-v3-text-2 hover:bg-v3-surface-2 hover:text-v3-text`,
  link: "font-v3 text-v3-brand-strong",
} as const;
export const V3_BUTTON_SIZE = {
  xl: "rounded-full text-[15px] font-semibold",
  lg: "rounded-full text-[15px]",
  sm: "rounded-full text-[13px]",
  default: "text-[14px]",
} as const;

// The v3.2 classes for a Button variant and size (unknown ones add nothing).
export const v3ButtonClasses = (
  variant: string | null | undefined,
  size: string | null | undefined,
): string =>
  `${(V3_BUTTON_VARIANT as Record<string, string | undefined>)[variant ?? "default"] ?? ""} ${(V3_BUTTON_SIZE as Record<string, string | undefined>)[size ?? "default"] ?? ""}`;

export const V3_OVERLAY = "bg-black/30";
// A centred card from 640px; on phones a bottom sheet (as V3ConfirmSheet).
export const V3_DIALOG =
  "v3-remap border-0 bg-v3-panel font-v3 text-v3-text shadow-v3-pop sm:rounded-[18px] max-sm:top-auto max-sm:bottom-0 max-sm:max-h-[92dvh] max-sm:translate-y-0 max-sm:overflow-y-auto max-sm:rounded-t-[22px] max-sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]";
export const V3_DIALOG_TITLE =
  "text-[17px] font-semibold leading-6 tracking-normal text-v3-text";
export const V3_DIALOG_DESCRIPTION =
  "text-[14px] leading-relaxed text-v3-text-2";
export const V3_SHEET =
  "v3-remap border-0 bg-v3-panel font-v3 text-v3-text shadow-v3-pop";
export const V3_POPOVER =
  "v3-remap rounded-xl border-0 bg-v3-panel font-v3 text-v3-text shadow-v3-pop";
// Menus and selects take their v3.2 classes from @rosc/ui
// (packages/ui/src/v32/menu.tsx, select.tsx).
export const V3_MENU_PANEL = MENU_PANEL;
export const V3_MENU_ROW = MENU_ROW;
export const V3_MENU_LABEL_TEXT = MENU_LABEL_TEXT;
export const V3_MENU_RULE = MENU_RULE;
export const V3_INPUT =
  "rounded-[10px] border-0 bg-v3-surface-2 font-v3 text-[15px] text-v3-text shadow-v3-hairline placeholder:text-v3-text-3 focus-visible:ring-2 focus-visible:ring-v3-brand-ring";
export const V3_SELECT_TRIGGER = SELECT_TRIGGER;
export const V3_SWITCH =
  "focus-visible:ring-v3-brand-ring data-[state=checked]:bg-v3-ink data-[state=unchecked]:bg-v3-surface-3";
export const V3_SWITCH_THUMB = "bg-v3-panel";
export const V3_CHECKBOX =
  "rounded-[5px] border-v3-border-strong data-[state=checked]:border-v3-ink data-[state=checked]:bg-v3-ink data-[state=checked]:text-v3-on-ink";
export const V3_LABEL = "font-v3 text-v3-text-2";

// A neutral icon tile (v3.2: surface with an ink icon, no brand tint).
export const V3_ICON_TILE = "bg-v3-surface-2 text-v3-text shadow-v3-hairline";
// A text link in the v3.2 app: ink, underlined on hover.
export const V3_LINK =
  "text-v3-text underline-offset-4 hover:text-v3-text hover:underline";
